import { css, html, nothing } from 'lit';
import { customElement, state } from 'lit/decorators.js';
import { t } from 'src/i18n';
import { BaseDialog } from 'src/library/components/BaseDialog';
import '../../../library/elements/d-action.js';
import '../../../library/fields/d-spinner.js';
import {
  detectBleCapability,
  isBleProvisioningSupported,
} from 'src/store/signage/bluetooth-provisioning/ble-capability';
import { createProvisioningTransport } from 'src/store/signage/bluetooth-provisioning/create-provisioning-transport';
import type { ProvisioningTransport } from 'src/store/signage/bluetooth-provisioning/provisioning-transport';
import type {
  BleCapability,
  ProvisioningStatus,
  WifiNetwork,
} from 'src/store/signage/bluetooth-provisioning/provisioning-types';
import type { NetworkConfigDialogInput } from './network-config-dialog-input';
import type { NetworkConfigDialogResult } from './network-config-dialog-result';

type Phase = 'unsupported' | 'intro' | 'connecting' | 'select' | 'credentials' | 'applying' | 'success' | 'error';

/** Maks ventetid på terminal status fra enheten før vi gir opp. */
const APPLY_TIMEOUT_MS = 40000;

/**
 * Dialog for å konfigurere nettverk på en tilkoblet infoskjerm-player via
 * Bluetooth (BLE). Starter Web Bluetooth i Chrome o.l.; faller tilbake til en
 * tydelig feilmelding der BLE ikke støttes (Safari/iOS-nettleser, og dagens
 * native-app uten BLE-plugin).
 *
 * Bruker spillerkoden fra entityView (playerCode); boksen annonserer navnet
 * `Trinnvis-<KODE>` og finnes via Improv-service-UUID-en. Snakker mot den
 * bekreftede GATT-kontrakten – se
 * src/store/signage/bluetooth-provisioning/provisioning-contract.ts.
 */
@customElement('d-screen-network-config-dialog')
export class DScreenNetworkConfigDialog extends BaseDialog<NetworkConfigDialogInput, NetworkConfigDialogResult> {
  static readonly styles = [
    ...BaseDialog.styles,
    css`
      .body-text {
        font-size: 15px;
        line-height: 1.5;
        margin-bottom: 16px;
      }
      .spinner-area {
        position: relative;
        min-height: 80px;
        margin: 16px 0;
      }
      .field {
        display: block;
        margin-bottom: 12px;
      }
      .field label {
        display: block;
        font-size: 13px;
        font-weight: bold;
        margin-bottom: 4px;
      }
      .field input {
        width: 100%;
        box-sizing: border-box;
        padding: 8px 10px;
        font-size: 16px;
        border: 1px solid var(--borderColor, silver);
        border-radius: 6px;
      }
      .network-list {
        list-style: none;
        margin: 0 0 12px 0;
        padding: 0;
        border: 1px solid var(--borderColor, silver);
        border-radius: 6px;
        overflow: hidden;
      }
      .network-list li {
        display: flex;
        justify-content: space-between;
        align-items: center;
        gap: 8px;
        padding: 10px 12px;
        cursor: pointer;
        border-bottom: 1px solid var(--borderColor, #eee);
      }
      .network-list li:last-child {
        border-bottom: none;
      }
      .network-list li:hover {
        background: var(--backgroundGray, #f4f4f4);
      }
      .network-list .meta {
        display: flex;
        align-items: center;
        gap: 8px;
        font-size: 12px;
        color: gray;
      }
      .signal-bars {
        display: inline-flex;
        align-items: flex-end;
        gap: 2px;
        height: 14px;
      }
      .signal-bars .bar {
        width: 3px;
        background: var(--borderColor, #ccc);
        border-radius: 1px;
      }
      .signal-bars .bar.on {
        background: var(--themeColorDarkerThree, #4a90d9);
      }
      .signal-bars .bar:nth-child(1) {
        height: 4px;
      }
      .signal-bars .bar:nth-child(2) {
        height: 7px;
      }
      .signal-bars .bar:nth-child(3) {
        height: 10px;
      }
      .signal-bars .bar:nth-child(4) {
        height: 14px;
      }
      .hint-text {
        font-size: 13px;
        color: gray;
        margin-top: 12px;
      }
      .error-text {
        color: var(--alertColor, #c0392b);
      }
      .actions {
        display: flex;
        flex-wrap: wrap;
        gap: 8px;
        justify-content: flex-end;
        padding-top: 8px;
      }
    `,
  ];

  @state() private phase: Phase = 'intro';
  @state() private capability: BleCapability = 'unsupported';
  @state() private networks: WifiNetwork[] = [];
  @state() private selectedSsid = '';
  @state() private selectedSecured = true;
  @state() private password = '';
  @state() private manualEntry = false;
  @state() private errorMessage = '';
  @state() private statusDetail = '';
  @state() private busy = false;

  private screenName = '';
  private deviceCode = '';
  private transport?: ProvisioningTransport;
  private statusUnsub?: () => void;
  private applyTimer?: ReturnType<typeof setTimeout>;
  private outcome: 'done' | 'cancel' = 'cancel';
  private connected = false;

  protected get calculatedTitle(): string {
    return t('screens.networkConfig.title');
  }

  protected get calculatedSubtitle(): string {
    return this.screenName;
  }

  protected initializeDialog(input: NetworkConfigDialogInput): void {
    // Uten standard-footer; all navigasjon skjer med knapper i body slik at
    // stegene ikke lukker dialogen. X-lukkeren vises og fungerer som Avbryt.
    this.noFooter = true;
    this.screenName = input.screenName ?? '';
    this.deviceCode = input.deviceCode ?? '';
    this.capability = detectBleCapability();
    if (!isBleProvisioningSupported(this.capability)) {
      this.phase = 'unsupported';
    } else if (!this.deviceCode) {
      // Uten en paret spiller har vi ingen enhet å be om over BLE.
      this.errorMessage = t('screens.networkConfig.errorNotPaired');
      this.phase = 'error';
    } else {
      this.phase = 'intro';
    }
  }

  protected fetchResult(): NetworkConfigDialogResult {
    return { action: this.outcome, connected: this.connected };
  }

  override onClose(e: CustomEvent): void {
    this.cleanup();
    super.onClose(e);
  }

  private cleanup(): void {
    if (this.applyTimer) {
      clearTimeout(this.applyTimer);
      this.applyTimer = undefined;
    }
    this.statusUnsub?.();
    this.statusUnsub = undefined;
    void this.transport?.disconnect();
    this.transport = undefined;
  }

  private async startConnect(): Promise<void> {
    this.busy = true;
    this.errorMessage = '';
    this.phase = 'connecting';
    try {
      console.info(
        `[Signage BLE] Kobler til skjerm «${this.screenName || '(uten navn)'}» ` +
          `(spillerkode ${this.deviceCode}) via ${this.capability}.`,
      );
      this.transport = createProvisioningTransport(this.capability);
      await this.transport.connect(this.deviceCode);
      this.networks = await this.transport.scanWifi();
      this.manualEntry = this.networks.length === 0;
      console.info(`[Signage BLE] Scan ga ${this.networks.length} nett; manuell inntasting=${this.manualEntry}.`);
      this.phase = 'select';
    } catch (err) {
      this.showError(this.describeConnectError(err));
    } finally {
      this.busy = false;
    }
  }

  private chooseNetwork(network: WifiNetwork): void {
    this.selectedSsid = network.ssid;
    this.selectedSecured = network.secured ?? true;
    this.password = '';
    if (this.selectedSecured) {
      this.phase = 'credentials';
    } else {
      void this.submitCredentials();
    }
  }

  private chooseManualNetwork(): void {
    if (!this.selectedSsid.trim()) return;
    this.selectedSecured = true;
    this.phase = 'credentials';
  }

  private async submitCredentials(): Promise<void> {
    if (!this.transport) {
      this.showError(t('screens.networkConfig.errorNotConnected'));
      return;
    }
    this.busy = true;
    this.errorMessage = '';
    this.statusDetail = '';
    this.phase = 'applying';
    try {
      this.statusUnsub = this.transport.onStatus((status) => this.handleStatus(status));
      this.applyTimer = setTimeout(() => {
        if (this.phase === 'applying') {
          this.showError(t('screens.networkConfig.errorTimeout'));
        }
      }, APPLY_TIMEOUT_MS);
      await this.transport.submitCredentials({
        ssid: this.selectedSsid.trim(),
        psk: this.password,
      });
    } catch (err) {
      this.showError(err instanceof Error ? err.message : t('screens.networkConfig.errorSubmitFailed'));
    } finally {
      this.busy = false;
    }
  }

  private handleStatus(status: ProvisioningStatus): void {
    if (this.phase !== 'applying') return;
    if (status.state === 'connected') {
      if (this.applyTimer) clearTimeout(this.applyTimer);
      this.connected = true;
      this.outcome = 'done';
      this.statusDetail = status.ip
        ? t('screens.networkConfig.statusConnectedIp', { ip: status.ip })
        : t('screens.networkConfig.statusConnected');
      this.phase = 'success';
    } else if (status.state === 'failed') {
      this.showError(status.detail || t('screens.networkConfig.errorConnectFailed'));
    }
  }

  private showError(message: string): void {
    if (this.applyTimer) clearTimeout(this.applyTimer);
    this.errorMessage = message;
    this.phase = 'error';
  }

  private describeConnectError(err: unknown): string {
    // requestDevice kaster NotFoundError når brukeren lukker velgeren uten valg.
    if (err instanceof DOMException && err.name === 'NotFoundError') {
      return t('screens.networkConfig.errorNoDeviceSelected');
    }
    if (err instanceof Error && err.message) {
      return err.message;
    }
    return t('screens.networkConfig.errorConnectGeneric');
  }

  private finish(): void {
    this.close();
  }

  private renderUnsupported() {
    const network = `Trinnvis-${(this.deviceCode || '').toUpperCase()}`;
    return html`
      <p class="body-text error-text">${t('screens.networkConfig.unsupportedText', { network })}</p>
      <div class="actions">
        <d-action plain button @click=${() => this.finish()}>${t('screens.networkConfig.close')}</d-action>
      </div>
    `;
  }

  /** WiFi-signal som 4 linjer (0–4 påslått) basert på styrke 0–100. */
  private renderSignalBars(signal?: number) {
    if (signal === undefined) return nothing;
    const active = signal >= 75 ? 4 : signal >= 50 ? 3 : signal >= 25 ? 2 : signal > 0 ? 1 : 0;
    return html`
      <span
        class="signal-bars"
        title="${t('screens.networkConfig.signalLabel')}"
        aria-label="${t('screens.networkConfig.signalLabel')}"
      >
        ${[1, 2, 3, 4].map((i) => html`<span class="bar ${i <= active ? 'on' : ''}"></span>`)}
      </span>
    `;
  }

  private renderIntro() {
    const network = `Trinnvis-${(this.deviceCode || '').toUpperCase()}`;
    return html`
      <p class="body-text">${t('screens.networkConfig.introText', { network })}</p>
      <div class="actions">
        <d-action plain @click=${() => this.finish()}>${t('screens.networkConfig.cancel')}</d-action>
        <d-action plain button ?disabled=${this.busy} @click=${() => this.startConnect()}>
          ${t('screens.networkConfig.connectToScreen')}
        </d-action>
      </div>
    `;
  }

  private renderConnecting() {
    return html`
      <p class="body-text">${t('screens.networkConfig.connecting')}</p>
      <div class="spinner-area"><d-spinner size="medium"></d-spinner></div>
    `;
  }

  private renderSelect() {
    return html`
      ${this.networks.length > 0 && !this.manualEntry
        ? html`
            <p class="body-text">${t('screens.networkConfig.selectNetwork')}</p>
            <ul class="network-list">
              ${this.networks.map(
                (network) => html`
                  <li @click=${() => this.chooseNetwork(network)}>
                    <span>${network.ssid}</span>
                    <span class="meta">
                      <span
                        >${network.secured === false
                          ? t('screens.networkConfig.networkOpen')
                          : t('screens.networkConfig.networkSecured')}</span
                      >
                      ${this.renderSignalBars(network.signal)}
                    </span>
                  </li>
                `,
              )}
            </ul>
            <div class="actions">
              <d-action plain @click=${() => (this.manualEntry = true)}>
                ${t('screens.networkConfig.enterManually')}
              </d-action>
            </div>
          `
        : html`
            <p class="body-text">${t('screens.networkConfig.enterSsidPrompt')}</p>
            <label class="field">
              <span>${t('screens.networkConfig.ssidLabel')}</span>
              <input
                type="text"
                .value=${this.selectedSsid}
                @input=${(e: Event) => (this.selectedSsid = (e.target as HTMLInputElement).value)}
              />
            </label>
            <div class="actions">
              ${this.networks.length > 0
                ? html`<d-action plain @click=${() => (this.manualEntry = false)}>
                    ${t('screens.networkConfig.backToList')}
                  </d-action>`
                : nothing}
              <d-action plain button ?disabled=${!this.selectedSsid.trim()} @click=${() => this.chooseManualNetwork()}>
                ${t('screens.networkConfig.next')}
              </d-action>
            </div>
          `}
    `;
  }

  private renderCredentials() {
    return html`
      <p class="body-text">${t('screens.networkConfig.passwordPrompt', { ssid: this.selectedSsid })}</p>
      <label class="field">
        <span>${t('screens.networkConfig.passwordLabel')}</span>
        <input
          type="password"
          .value=${this.password}
          @input=${(e: Event) => (this.password = (e.target as HTMLInputElement).value)}
        />
      </label>
      <div class="actions">
        <d-action plain @click=${() => (this.phase = 'select')}>${t('screens.networkConfig.back')}</d-action>
        <d-action plain button ?disabled=${this.busy || !this.password} @click=${() => this.submitCredentials()}>
          ${t('screens.networkConfig.connect')}
        </d-action>
      </div>
    `;
  }

  private renderApplying() {
    return html`
      <p class="body-text">${t('screens.networkConfig.applying')}</p>
      <div class="spinner-area"><d-spinner size="medium"></d-spinner></div>
      <p class="hint-text">${t('screens.networkConfig.resetHint')}</p>
    `;
  }

  private renderSuccess() {
    return html`
      <p class="body-text">${t('screens.networkConfig.successText', { detail: this.statusDetail })}</p>
      <div class="actions">
        <d-action plain button @click=${() => this.finish()}>${t('screens.networkConfig.done')}</d-action>
      </div>
    `;
  }

  private renderError() {
    return html`
      <p class="body-text error-text">${this.errorMessage}</p>
      <div class="actions">
        <d-action plain @click=${() => this.finish()}>${t('screens.networkConfig.close')}</d-action>
        <d-action plain button @click=${() => this.startConnect()}>${t('screens.networkConfig.retry')}</d-action>
      </div>
    `;
  }

  protected renderBody() {
    switch (this.phase) {
      case 'unsupported':
        return this.renderUnsupported();
      case 'intro':
        return this.renderIntro();
      case 'connecting':
        return this.renderConnecting();
      case 'select':
        return this.renderSelect();
      case 'credentials':
        return this.renderCredentials();
      case 'applying':
        return this.renderApplying();
      case 'success':
        return this.renderSuccess();
      case 'error':
        return this.renderError();
    }
  }
}

declare global {
  interface HTMLElementTagNameMap {
    'd-screen-network-config-dialog': DScreenNetworkConfigDialog;
  }
}
